import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import type {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import type { WipColumnConfigI } from 'reduxStore/features/wip/wipSlice';

import { WIP_ITEM_COLUMN_CONFIGURATION } from '../WIPTable/WIPItemsTableConfiguration';

export interface WipColumnPickerPropsI {
  columns: WipColumnConfigI[];
  onOrderChange: (result: DropResult) => void;
  onVisibilityToggle: (columnId: string) => void;
  onResetConfig: () => void;
  onSaveConfig: () => void;
}

const labelById = new Map<string, string>(
  WIP_ITEM_COLUMN_CONFIGURATION.map((c): [string, string] => [c.id, c.label])
);

const getLabel = (id: string): string => labelById.get(id) ?? id;

/**
 * Horizontal, drag-and-drop column configuration UI for the WIP table. Each column renders as
 * a card with a drag handle, label, and visibility switch. Required columns disable the switch
 * so the user cannot hide them. Mirrors WarrQueue's `ColumPicker` component used inside its WIP
 * "Customize Columns" modal.
 */
const WipColumnPicker: FC<WipColumnPickerPropsI> = ({
  columns,
  onOrderChange,
  onVisibilityToggle,
  onResetConfig,
  onSaveConfig,
}): JSX.Element => {
  // Custom drag clone keeps the dragging card aligned with the cursor inside scrollable parents.
  const renderClone = (
    provided: DraggableProvided,
    _snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric
  ): JSX.Element => {
    const column = columns[rubric.source.index];
    const isRequired = column.required === true;
    return (
      <Box
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        sx={{
          ...provided.draggableProps.style,
          padding: '4px 6px',
          border: (theme): string => `1px solid ${theme.palette.grey[400]}`,
          borderRadius: '8px',
          backgroundColor: (theme): string => theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box display="flex" justifyContent="center">
          <DragIndicatorIcon sx={{ fontSize: '16px' }} />
        </Box>
        <Typography
          variant="caption"
          textAlign="center"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}
        >
          {getLabel(column.id)}
        </Typography>
        {!isRequired ? (
          <Switch
            checked={column.visible}
            size="small"
            sx={{ cursor: 'pointer', mt: -0.5 }}
            onChange={(): void => onVisibilityToggle(column.id)}
          />
        ) : (
          <Box sx={{ height: 28 }} />
        )}
      </Box>
    );
  };

  return (
    <Box py={2} px={4}>
      <Box
        m="5px 0px"
        sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <DragDropContext onDragEnd={onOrderChange}>
          <Droppable droppableId="wip-columns" direction="horizontal" renderClone={renderClone}>
            {(provided): JSX.Element => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ display: 'flex', gap: 0.5 }}
              >
                {columns.map((column, index): JSX.Element => {
                  const isRequired = column.required === true;
                  return (
                    <Draggable
                      key={`wip-col-${column.id}`}
                      draggableId={`wip-col-${column.id}`}
                      index={index}
                    >
                      {(dragProvided, snapshot): JSX.Element => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          sx={{
                            padding: '4px 6px',
                            border: snapshot.isDragging
                              ? 'none'
                              : (theme): string => `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: '8px',
                            backgroundColor: snapshot.isDragging
                              ? (theme): string => theme.palette.grey[100]
                              : 'transparent',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Box display="flex" justifyContent="center">
                            <DragIndicatorIcon sx={{ fontSize: '16px' }} />
                          </Box>
                          <Typography
                            variant="caption"
                            textAlign="center"
                            sx={{
                              fontSize: '0.75rem',
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {getLabel(column.id)}
                          </Typography>
                          {!isRequired ? (
                            <Switch
                              checked={column.visible}
                              size="small"
                              sx={{ cursor: 'pointer', mt: -0.5 }}
                              onChange={(): void => onVisibilityToggle(column.id)}
                            />
                          ) : (
                            <Box sx={{ height: 28 }} />
                          )}
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <Box mt={3} display="flex" justifyContent="flex-end" gap="10px">
        <Button onClick={onResetConfig}>Reset</Button>
        <Button color="primary" variant="contained" onClick={onSaveConfig}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default WipColumnPicker;
